import React, { useState, useEffect } from "react";
import { Card, Row, Col, Select, Typography, Table, Spin, Tag, Button, message, Form } from "antd";
import axios from "axios";
import {
  DesktopOutlined,
  EnvironmentOutlined,
  ClusterOutlined,
  TabletOutlined,
  UserOutlined,
  PlaySquareOutlined,
  PictureOutlined,
  EyeOutlined,
  SyncOutlined,
  FilterOutlined,
} from "@ant-design/icons";

import Link from "antd/es/typography/Link";
import StatCard from "../components/Dashboard/StatCard";
import ClearFiltersButton from "../components/Common/ClearFiltersButton";
import { useNavigate, useLocation } from "react-router-dom";
import PreviewMedia from "../components/Library/PreviewMedia";
import PreviewPlaylist from "../components/Playlists/PreviewPlaylist";
import { getMediaIcon } from "../utils/Misc";

const { Title } = Typography;
const { Option } = Select;

const Dashboard = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const [loading, setLoading] = useState(false);
  const [selectedCity, setSelectedCity] = useState("");
  const [selectedLocation, setSelectedLocation] = useState("");
  const [userViewPermission, setUserViewPermission] = useState(false);
  const [citiesViewPermission, setCitiesViewPermission] = useState(false);
  const [locationsViewPermission, setLocationsViewPermission] = useState(false);
  const [mediaData, setMediaData] = useState([]);
  const [playlistsData, setPlaylistsData] = useState([]);
  const [locations, setLocations] = useState([]);
  const [cityOptions, setCityOptions] = useState([]);
  const [locationOptions, setLocationOptions] = useState([]);
  const [screens, setScreens] = useState(0);
  const [devices, setDevices] = useState(0);
  const [users, setUsers] = useState(0);
  const [totalLocations, setTotalLocations] = useState(0);
  const [media, setMedia] = useState(0);
  const [playlists, setPlaylists] = useState(0);
  const [mediaPending, setMediaPending] = useState(0);
  const [playlistsPending, setPlaylistsPending] = useState(0);
  const [dashboardData, setDashboardData] = useState([]);
  const [isPreviewModalOpen, setIsPreviewModalOpen] = useState(false);
  const [isPlaylistPreviewModalOpen, setIsPlaylistPreviewModalOpen] = useState(false);
  const [previewMedia, setPreviewMedia] = useState(null);
  const [previewPlaylist, setPreviewPlaylist] = useState(null);
  const [syncLoading, setSyncLoading] = useState(false);
  const [showFilters, setShowFilters] = useState(false);

  const setQueryParams = (params) => {
    const searchParams = new URLSearchParams(params);
    navigate(`?${searchParams.toString()}`, { replace: true });
  };

  const handleCityChange = (value) => {
    setSelectedCity(value || "");
    const filteredLocations = locations
      .filter((location) => location.city_id === value)
      .map((location) => ({
        id: location.id,
        name: location.name,
      }));
    if (filteredLocations.length === 0) {
      setLocationOptions(locations);
    } else {
      setLocationOptions(filteredLocations);
    }
    const newQueryParams = {
      city: value || "",
      location: selectedLocation || "",
    };

    setQueryParams(newQueryParams);
  };

  const handleLocationChange = (value) => {
    setSelectedLocation(value || "");
    const newQueryParams = {
      city: selectedCity || "",
      location: value || "",
    };
    setQueryParams(newQueryParams);
  };

  const handleClearFilters = () => {
    setSelectedCity("");
    setSelectedLocation("");
    setLocationOptions(locations);
    setQueryParams({ city: "", location: "" });
  };

  const handlePreviewMedia = (mediaData) => {
    setIsPreviewModalOpen(true);
    setPreviewMedia(mediaData);
  };

  const handlePreviewPlaylist = (record) => {
    setIsPlaylistPreviewModalOpen(true);
    setPreviewPlaylist({ ...record, media: JSON.parse(record.media_details) });
  };

  const handleSync = async () => {
    try {
      setSyncLoading(true);
      await axios.put(
        `${process.env.REACT_APP_BASE_URL}/api/dashboard/sync`
      );
      message.success("Dashboard synced successfully");
      await fetchData();
      setSyncLoading(false);
    } catch (err) {
      console.log(err);
    }
  };

  const fetchData = async (
    cityId = selectedCity || "",
    locationId = selectedLocation || ""
  ) => {
    try {
      setLoading(true);
      const response = await axios.get(
        process.env.REACT_APP_BASE_URL + `/api/dashboard`,
        {
          params: {
            city: cityId,
            location: locationId,
          },
        }
      );
      let dashboardData = response.data.data;
      setScreens(dashboardData.total_screens);
      setDevices(dashboardData.total_devices);
      setUsers(dashboardData.total_users);
      setTotalLocations(dashboardData.total_locations);
      setPlaylists(dashboardData.total_playlists);
      setMedia(dashboardData.total_media);
      setPlaylistsPending(dashboardData.total_pending_playlists);
      setMediaPending(dashboardData.total_pending_media);
      setMediaData(dashboardData.media);
      setPlaylistsData(dashboardData.playlists);
      setDashboardData(dashboardData);
    } catch (error) {
      console.error("Error fetching cities data", error);
    } finally {
      setLoading(false);
    }
  };

  const fetchLocations = async () => {
    try {
      const response = await axios.get(
        process.env.REACT_APP_BASE_URL + `/api/locations`,
        { params: { limit: 1000 } }
      );
      const location = response.data.data.data;
      let city = [];
      for (let i = 0; i < location.length; i++) {
        city.push({
          id: location[i].city_id,
          name: location[i].city_name,
        });
      }
      const uniqueCities = Array.from(
        new Map(city.map((item) => [item.id, item])).values()
      );
      setLocations(location);
      setCityOptions(uniqueCities);
      if (!selectedCity) {
        setLocationOptions(location);
      } else {
        const filteredLocations = location.filter(
          (loc) => loc.city_id === selectedCity
        );
        setLocationOptions(
          filteredLocations.length > 0 ? filteredLocations : location
        );
      }
    } catch (error) {
      console.error("Error fetching locations", error);
    }
  };

  useEffect(() => {
    fetchLocations();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Restore city/location from the URL on load (e.g. a link from another
  // page), same as the Locations page does with its filters.
  useEffect(() => {
    const cityParam = queryParams.get("city");
    const locationParam = queryParams.get("location");
    if (cityParam && cityParam !== "" && !isNaN(cityParam)) {
      setSelectedCity(parseInt(cityParam));
    }
    if (locationParam && locationParam !== "" && !isNaN(locationParam)) {
      setSelectedLocation(parseInt(locationParam));
    }
    if (cityParam || locationParam) {
      setShowFilters(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const newQueryParams = {
      city: selectedCity || "",
      location: selectedLocation || "",
    };

    if (
      JSON.stringify(newQueryParams) !==
      JSON.stringify(Object.fromEntries(new URLSearchParams(location.search)))
    ) {
      setQueryParams(newQueryParams);
    }

    setQueryParams(newQueryParams);
    const dashboardTimeout = setTimeout(() => {
      fetchData(selectedCity || "", selectedLocation || "");
    }, 500);

    return () => clearTimeout(dashboardTimeout);
  }, [selectedCity, selectedLocation]);

  const stats = {
    screens: screens ?? 0,
    cities: cityOptions?.length ?? 0,
    locations: totalLocations ?? 0,
    devices: devices ?? 0,
    users: users ?? 0,
    playlists: playlists ?? 0,
    media: media ?? 0,
  };

  // Selecting a city alone, or a parent (top-level) location, still leaves
  // "how many locations" meaningful (it becomes a count of that location's
  // floors) - only hide the Locations card once a floor itself is selected.
  // The Cities card stops being meaningful as soon as either filter narrows
  // things down to a single city/location.
  const hideCitiesCard = selectedCity || selectedLocation;
  const selectedLocationInfo = locations.find((loc) => loc.id === selectedLocation);
  const hideLocationsCard = selectedLocation && !!selectedLocationInfo?.parent_id;

  useEffect(() => {
    const user = JSON.parse(localStorage.getItem("userData"));
    const Permissions = user.permissions;
    setUserViewPermission(Permissions["users.view"] === "1");
    setCitiesViewPermission(Permissions["cities.view"] === "1");
    setLocationsViewPermission(Permissions["locations.view"] === "1");
  }, []);

  return (
    <Spin spinning={loading} tip="Loading..." size="small">
      <div style={{ padding: "20px" }}>
        <Title level={2} style={{ marginBottom: "20px" }}>
          Dashboard
        </Title>

        <div className="d-sm-block d-md-flex justify-content-between mb-3">
          <Form layout="inline">
            <Form.Item>
              <Button
                icon={<FilterOutlined />}
                type={showFilters ? "primary" : "default"}
                onClick={() => setShowFilters((prev) => !prev)}
              >
                Filters
                {(selectedCity || selectedLocation) && (
                  <Tag color="blue" style={{ marginLeft: 6, marginRight: 0 }}>
                    {[selectedCity, selectedLocation].filter(Boolean).length}
                  </Tag>
                )}
              </Button>
            </Form.Item>
          </Form>
          <Button
            icon={<SyncOutlined />}
            onClick={() => handleSync()}
            loading={syncLoading}
            disabled={syncLoading}
            style={{ backgroundColor: "#0b59c0ff", color: "white" }}
          >
            Sync
          </Button>
        </div>

        {showFilters && (
          <Form layout="inline" className="mb-3">
            <Form.Item label="City">
              <Select
                value={selectedCity}
                placeholder="Filter by City"
                style={{ width: "200px" }}
                onChange={handleCityChange}
                showSearch
                allowClear
                filterOption={(input, option) => {
                  return (option?.children ?? "")
                    .toString()
                    .toLowerCase()
                    .includes(input.toLowerCase());
                }}
              >
                {cityOptions.map((city) => (
                  <Option key={city.id} value={city.id}>
                    {city.name}
                  </Option>
                ))}
              </Select>
            </Form.Item>
            <Form.Item label="Location">
              <Select
                value={selectedLocation}
                placeholder="Filter by Location"
                style={{ width: "200px" }}
                onChange={handleLocationChange}
                showSearch
                allowClear
                filterOption={(input, option) => {
                  return (option?.children ?? "")
                    .toString()
                    .toLowerCase()
                    .includes(input.toLowerCase());
                }}
              >
                {locationOptions.map((loc) => (
                  <Option key={loc.id} value={loc.id}>
                    {loc.name}
                  </Option>
                ))}
              </Select>
            </Form.Item>
            <Form.Item style={{ marginLeft: "auto", alignSelf: "flex-end" }}>
              <ClearFiltersButton onClick={handleClearFilters} />
            </Form.Item>
          </Form>
        )}

        <Row gutter={[16, 16]}>
          <Col xs={24} sm={12} md={6}>
            <StatCard
              title="Screens"
              value={stats.screens}
              icon={
                <DesktopOutlined
                  style={{ fontSize: "28px", color: "#1890ff" }}
                />
              }
              link={`${process.env.REACT_APP_URL}/screens?city=${selectedCity}&location=${selectedLocation}`}
            />
          </Col>
          {citiesViewPermission && !hideCitiesCard && (
            <Col xs={24} sm={12} md={6}>
              <StatCard
                title="Cities"
                value={stats.cities}
                icon={
                  <EnvironmentOutlined
                    style={{ fontSize: "28px", color: "#52c41a" }}
                  />
                }
                link={`${process.env.REACT_APP_URL}/cities`}
              />
            </Col>
          )}
          {locationsViewPermission && !hideLocationsCard && (
            <Col xs={24} sm={12} md={6}>
              <StatCard
                title="Locations"
                value={stats.locations}
                icon={
                  <ClusterOutlined
                    style={{ fontSize: "28px", color: "#faad14" }}
                  />
                }
                link={`${process.env.REACT_APP_URL}/locations?status=active${selectedCity ? `&city=${selectedCity}` : ""}${selectedLocation ? `&building=${selectedLocation}` : ""}`}
              />
            </Col>
          )}
          <Col xs={24} sm={12} md={6}>
            <StatCard
              title="Signage TVs"
              value={stats.devices}
              icon={
                <TabletOutlined
                  style={{ fontSize: "28px", color: "#eb2f96" }}
                />
              }
              link={`${process.env.REACT_APP_URL}/signage-tvs?city=${selectedCity}&location=${selectedLocation}`}
            />
          </Col>
          {userViewPermission && (
            <Col xs={24} sm={12} md={6}>
              <StatCard
                title="Users"
                value={stats.users}
                icon={
                  <UserOutlined
                    style={{ fontSize: "28px", color: "#722ed1" }}
                  />
                }
                link={`${process.env.REACT_APP_URL}/users?city=${selectedCity}&location=${selectedLocation}`}
              />
            </Col>
          )}
          <Col xs={24} sm={12} md={6}>
            <StatCard
              title="Playlists"
              value={stats.playlists}
              icon={
                <PlaySquareOutlined
                  style={{ fontSize: "28px", color: "#722ed1" }}
                />
              }
              link={`${process.env.REACT_APP_URL}/playlists`}
            />
          </Col>
          <Col xs={24} sm={12} md={6}>
            <StatCard
              title="Media"
              value={stats.media}
              icon={
                <PictureOutlined
                  style={{ fontSize: "28px", color: "#722ed1" }}
                />
              }
              link={`${process.env.REACT_APP_URL}/library?status=1`}
            />
          </Col>
          <Col xs={24} sm={12} md={6}>
            <StatCard
              title="Pending Playlists"
              value={playlistsPending}
              icon={
                <PlaySquareOutlined
                  style={{ fontSize: "28px", color: "#722ed1" }}
                />
              }
              link={`${process.env.REACT_APP_URL}/playlists?status=0`}
            />
          </Col>
          <Col xs={24} sm={12} md={6}>
            <StatCard
              title="Pending Media"
              value={mediaPending}
              icon={
                <PictureOutlined
                  style={{ fontSize: "28px", color: "#722ed1" }}
                />
              }
              link={`${process.env.REACT_APP_URL}/library?status=0`}
            />
          </Col>
        </Row>

        <Row gutter={[16, 16]} style={{ marginTop: "20px" }}>
          <Col xs={24}>
            <Card title="Top Playlists">
              <Table
                dataSource={playlistsData}
                rowKey="id"
                pagination={false}
                columns={[
                  { title: "Name", dataIndex: "title" },
                  { title: "No. of times played", dataIndex: "play_count" },
                  {
                    title: "Preview",
                    dataIndex: "media",
                    render: (text, record) => (
                      <Link
                        href="#"
                        onClick={() => handlePreviewPlaylist(record)}
                      >
                        <Tag style={{ color: "#047b1eff" }}>
                          {<EyeOutlined style={{ marginRight: "5px" }} />}
                          Preview
                        </Tag>
                      </Link>
                    ),
                  },
                ]}
              />
              {isPlaylistPreviewModalOpen && (
                <PreviewPlaylist
                  isModalOpen={isPlaylistPreviewModalOpen}
                  closeModal={() => setIsPlaylistPreviewModalOpen(false)}
                  playlist={previewPlaylist}
                />
              )}
            </Card>
          </Col>
        </Row>

        <Row gutter={[16, 16]} style={{ marginTop: "20px" }}>
          <Col xs={24}>
            <Card title="Top Media">
              <Table
                dataSource={mediaData}
                rowKey="id"
                pagination={false}
                columns={[
                  { title: "Name", dataIndex: "title" },
                  { title: "No. of times played", dataIndex: "play_count" },
                  {
                    title: "Preview",
                    dataIndex: "media_type",
                    render: (text, record) => (
                      <Link href="#" onClick={() => handlePreviewMedia(record)}>
                        <Tag>{getMediaIcon(text)} Preview</Tag>
                      </Link>
                    ),
                  },
                ]}
              />
              {isPreviewModalOpen && (
                <PreviewMedia
                  isModalOpen={isPreviewModalOpen}
                  closeModal={() => setIsPreviewModalOpen(false)}
                  media={previewMedia}
                />
              )}
            </Card>
          </Col>
        </Row>
      </div>
    </Spin>
  );
};

export default Dashboard;
